import React, { useEffect, useState } from "react";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { TrendUp, Warning } from "@phosphor-icons/react";
import api from "../lib/api";

const TYPE_LABEL = {
  desayuno: "Desayuno",
  almuerzo: "Almuerzo",
  merienda: "Merienda",
  cena: "Cena",
};

// Genera un highlight humano del punto de dolor semanal
function buildInsight(data) {
  if (!data?.by_meal_type?.length) return null;
  const worst = data.by_meal_type.find((m) => m.meal_type === data.worst_meal_type)
    || data.by_meal_type.reduce((a, b) => (b.adherence_pct < a.adherence_pct ? b : a));
  if (!worst) return null;
  const label = TYPE_LABEL[worst.meal_type] || worst.meal_type;
  const skipped = worst.total_days - worst.days_logged;
  if (worst.days_logged === 0) {
    return { level: "danger", text: `Esta semana no cargaste ${worst.meal_type === "cena" || worst.meal_type === "merienda" ? "ninguna" : "ningún"} ${label.toLowerCase()}. 100% de los días sin registro.` };
  }
  if (skipped >= Math.ceil(worst.total_days / 2)) {
    return { level: "warn", text: `Te salteaste ${label.toLowerCase()} en ${skipped} de ${worst.total_days} días (${worst.skip_rate}%).` };
  }
  if (worst.adherence_pct < 60) {
    return { level: "warn", text: `${label}: adherencia de ${worst.adherence_pct}% esta semana — la más baja de tus comidas.` };
  }
  return { level: "ok", text: `Buena semana — tu peor tipo es ${label.toLowerCase()} con ${worst.adherence_pct}% de adherencia.` };
}

export default function WeeklyNutritionAdherence({ userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/users/${userId}/nutrition-weekly-adherence`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  if (loading) {
    return (
      <div className="card-base p-6" data-testid="weekly-adherence-loading">
        <div className="text-xs uppercase tracking-widest text-muted">Cargando adherencia semanal...</div>
      </div>
    );
  }
  if (!data || !data.by_meal_type?.length) return null;
  if (!data.daily_calories_target || data.daily_calories_target <= 0) return null;

  const chartData = data.by_meal_type.map((m) => ({
    type: TYPE_LABEL[m.meal_type] || m.meal_type,
    adherence: m.adherence_pct,
    objetivo: 100,
  }));

  const insight = buildInsight(data);

  return (
    <div className="card-base p-6" data-testid="weekly-adherence">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <TrendUp size={20} weight="duotone" className="text-primary-app" />
        <h3 className="font-display font-black uppercase text-xl tracking-tighter">Adherencia Semanal</h3>
        <span className="text-[10px] uppercase tracking-widest text-muted ml-1">últimos {data.total_days} días</span>
      </div>

      {insight && (
        <div
          className={`mb-5 p-3 border flex items-start gap-3 text-sm ${
            insight.level === "danger" ? "border-[#FF3B30] bg-[#FF3B30]/10" :
            insight.level === "warn" ? "border-[#FF9500] bg-[#FF9500]/10" :
            "border-[#32D74B] bg-[#32D74B]/10"
          }`}
          data-testid="weekly-insight"
        >
          <Warning size={18} weight="duotone" className="mt-0.5 flex-shrink-0" />
          <div>{insight.text}</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
        <div className="w-full h-64" data-testid="weekly-radar-chart">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={chartData} outerRadius="75%">
              <PolarGrid stroke="#262626" />
              <PolarAngleAxis dataKey="type" tick={{ fill: "#A3A3A3", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 2 }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "#525252", fontSize: 9 }} tickCount={5} />
              <Radar name="Objetivo" dataKey="objetivo" stroke="#262626" strokeDasharray="3 3" fill="#262626" fillOpacity={0.1} />
              <Radar name="Adherencia" dataKey="adherence" stroke="#FF3B30" fill="#FF3B30" fillOpacity={0.35} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <div className="space-y-2">
          {data.by_meal_type.map((m) => {
            const color = m.adherence_pct >= 75 ? "#32D74B" : m.adherence_pct >= 50 ? "#FFD60A" : m.adherence_pct >= 25 ? "#FF9500" : "#FF3B30";
            return (
              <div key={m.meal_type} className="bg-[#0A0A0A] border border-app p-3" data-testid={`adherence-row-${m.meal_type}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs uppercase tracking-widest font-bold">{TYPE_LABEL[m.meal_type] || m.meal_type}</div>
                  <div className="font-display font-black text-lg" style={{ color }}>{m.adherence_pct}%</div>
                </div>
                <div className="flex items-center justify-between text-[10px] text-muted mb-2">
                  <span>{m.days_logged}/{m.total_days} días</span>
                  <span>Promedio: {m.avg_consumed} / {m.target_kcal} kcal</span>
                </div>
                <div className="h-1 bg-[#262626] overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${Math.min(100, m.adherence_pct)}%`, backgroundColor: color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="text-[10px] text-muted mt-4">
        100% = adherencia óptima. Se aleja si consumís menos del objetivo o si lo superás (calculamos diferencia absoluta al objetivo).
      </div>
    </div>
  );
}
